import { useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import DeleteConfirmModal from '@/components/DeleteConfirmModal'
import { ArrowLeft, Save, Trash2, Check, X, DollarSign, PieChart, Split } from 'lucide-react'

interface Commission {
  id: number
  agent_id: number
  agent_name: string
  carrier_id: number
  carrier_name: string
  policy_number: string
  commission_type: string
  gross_amount: number
  received_amount: number | null
  agent_amount: number
  override_amount: number | null
  agent_split_percentage: number | null
  sponsor_split_percentage: number | null
  sponsor_amount: number | null
  supplement_amount: number
  carrier_payout_percentage: number | null
  parent_agent_id: number | null
  parent_agent_name: string | null
  period_start: string
  period_end: string
  status: string
  created_at: string
  pending_commission_id: number | null
}

interface Agent {
  id: number
  name: string
}

interface Carrier {
  id: number
  name: string
}

interface Props extends PageProps {
  commission: Commission
  agents: Agent[]
  carriers: Carrier[]
}

export default function CommissionShow({ commission, agents, carriers }: Props) {
  const { auth } = usePage<PageProps>().props
  const isAdmin = auth.user?.role === 'owner' || auth.user?.role === 'administrator'
  const canDelete = isAdmin
  
  const [isEditing, setIsEditing] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showSplitModal, setShowSplitModal] = useState(false)
  const [splitMode, setSplitMode] = useState<'percent' | 'dollar'>('percent')
  const [splitSecondAgentId, setSplitSecondAgentId] = useState<number | ''>('')
  const [splitPrimaryValue, setSplitPrimaryValue] = useState<number>(50)
  const [splitSecondaryValue, setSplitSecondaryValue] = useState<number>(50)
  const [splitSubmitting, setSplitSubmitting] = useState(false)
  const [splitError, setSplitError] = useState<string | null>(null)
  const [form, setForm] = useState({
    agent_id: commission.agent_id,
    carrier_id: commission.carrier_id,
    policy_number: commission.policy_number,
    commission_type: commission.commission_type,
    gross_amount: commission.gross_amount,
    agent_amount: commission.agent_amount,
    sponsor_amount: commission.sponsor_amount || 0,
    agent_split_percentage: commission.agent_split_percentage || 0,
    sponsor_split_percentage: commission.sponsor_split_percentage || 0,
    period_start: commission.period_start,
    period_end: commission.period_end,
    status: commission.status
  })

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-700',
    approved: 'bg-blue-100 text-blue-700',
    paid: 'bg-green-100 text-green-700'
  }

  const handleSave = () => {
    router.patch(`/commissions/${commission.id}`, { commission: form }, {
      onSuccess: () => setIsEditing(false)
    })
  }

  const handleApprove = () => {
    router.post(`/commissions/${commission.id}/approve`)
  }

  const canSplit = isAdmin && commission.status !== 'paid'

  const openSplitModal = () => {
    setSplitError(null)
    setSplitMode('percent')
    setSplitSecondAgentId('')
    setSplitPrimaryValue(50)
    setSplitSecondaryValue(50)
    setShowSplitModal(true)
  }

  const updateSplitPrimary = (value: number) => {
    setSplitPrimaryValue(value)
    if (splitMode === 'percent') {
      setSplitSecondaryValue(Math.max(0, Math.round((100 - value) * 100) / 100))
    } else {
      const total = commission.agent_amount
      setSplitSecondaryValue(Math.max(0, Math.round((total - value) * 100) / 100))
    }
  }

  const updateSplitSecondary = (value: number) => {
    setSplitSecondaryValue(value)
    if (splitMode === 'percent') {
      setSplitPrimaryValue(Math.max(0, Math.round((100 - value) * 100) / 100))
    } else {
      const total = commission.agent_amount
      setSplitPrimaryValue(Math.max(0, Math.round((total - value) * 100) / 100))
    }
  }

  const switchSplitMode = (mode: 'percent' | 'dollar') => {
    setSplitMode(mode)
    if (mode === 'percent') {
      setSplitPrimaryValue(50)
      setSplitSecondaryValue(50)
    } else {
      const half = Math.round((commission.agent_amount / 2) * 100) / 100
      setSplitPrimaryValue(half)
      setSplitSecondaryValue(Math.max(0, Math.round((commission.agent_amount - half) * 100) / 100))
    }
  }

  const submitSplit = () => {
    setSplitError(null)
    if (!splitSecondAgentId) {
      setSplitError('Choose a second agent.')
      return
    }
    setSplitSubmitting(true)
    router.post(`/commissions/${commission.id}/split`, {
      second_agent_id: splitSecondAgentId,
      mode: splitMode,
      primary_value: splitPrimaryValue,
      secondary_value: splitSecondaryValue
    }, {
      preserveScroll: true,
      onFinish: () => setSplitSubmitting(false),
      onSuccess: () => setShowSplitModal(false)
    })
  }

  const updateAgentAmount = (newAgentAmount: number) => {
    const gross = form.gross_amount
    const sponsorAmt = Math.max(0, gross - newAgentAmount)
    setForm({
      ...form,
      agent_amount: newAgentAmount,
      sponsor_amount: sponsorAmt,
      agent_split_percentage: gross > 0 ? Math.round((newAgentAmount / gross) * 10000) / 100 : 0,
      sponsor_split_percentage: gross > 0 ? Math.round((sponsorAmt / gross) * 10000) / 100 : 0
    })
  }

  const updateSponsorAmount = (newSponsorAmount: number) => {
    const gross = form.gross_amount
    const agentAmt = Math.max(0, gross - newSponsorAmount)
    setForm({
      ...form,
      agent_amount: agentAmt,
      sponsor_amount: newSponsorAmount,
      agent_split_percentage: gross > 0 ? Math.round((agentAmt / gross) * 10000) / 100 : 0,
      sponsor_split_percentage: gross > 0 ? Math.round((newSponsorAmount / gross) * 10000) / 100 : 0
    })
  }

  const formatCurrency = (amount: number) => `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`

  const hasSplit = commission.agent_split_percentage !== null && commission.agent_split_percentage > 0

  return (
    <AppLayout>
      <Head title={`Commission #${commission.policy_number}`} />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/commissions" className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Commission Details</h1>
              <p className="text-chestnut-charcoal/70 mt-1">Policy #{commission.policy_number}</p>
            </div>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              {commission.status === 'pending' && !isEditing && (
                <button onClick={handleApprove} className="btn-primary flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  Approve
                </button>
              )}
              {isEditing ? (
                <>
                  <button onClick={() => setIsEditing(false)} className="btn-secondary flex items-center gap-2">
                    <X className="h-4 w-4" />
                    Cancel
                  </button>
                  <button onClick={handleSave} className="btn-primary flex items-center gap-2">
                    <Save className="h-4 w-4" />
                    Save
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => setIsEditing(true)} className="btn-secondary">
                    Edit
                  </button>
                  {canSplit && (
                    <button onClick={openSplitModal} className="btn-secondary flex items-center gap-2">
                      <Split className="h-4 w-4" />
                      Split
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => setShowDeleteModal(true)} className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg">
                      <Trash2 className="h-5 w-5" />
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="card p-6 space-y-6">
              <div className="flex items-center gap-3 pb-4 border-b border-chestnut-charcoal/10">
                <div className="p-3 bg-chestnut-forest/10 rounded-lg">
                  <DollarSign className="h-6 w-6 text-chestnut-forest" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-chestnut-charcoal">Commission Information</h2>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[commission.status]}`}>
                    {commission.status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Agent</label>
                  {isEditing ? (
                    <select
                      value={form.agent_id}
                      onChange={(e) => setForm({ ...form, agent_id: Number(e.target.value) })}
                      className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg"
                    >
                      {agents.map((agent) => (
                        <option key={agent.id} value={agent.id}>{agent.name}</option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-chestnut-charcoal font-medium">
                      <Link href={`/agents/${commission.agent_id}`} className="text-chestnut-forest hover:underline">
                        {commission.agent_name}
                      </Link>
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Carrier</label>
                  {isEditing ? (
                    <select
                      value={form.carrier_id}
                      onChange={(e) => setForm({ ...form, carrier_id: Number(e.target.value) })}
                      className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg"
                    >
                      {carriers.map((carrier) => (
                        <option key={carrier.id} value={carrier.id}>{carrier.name}</option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-chestnut-charcoal">{commission.carrier_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Policy Number</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={form.policy_number}
                      onChange={(e) => setForm({ ...form, policy_number: e.target.value })}
                      className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg"
                    />
                  ) : (
                    <p className="text-chestnut-charcoal font-mono">{commission.policy_number}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Commission Type</label>
                  {isEditing ? (
                    <select
                      value={form.commission_type}
                      onChange={(e) => setForm({ ...form, commission_type: e.target.value })}
                      className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg"
                    >
                      <option value="first_year">First Year</option>
                      <option value="renewal">Renewal</option>
                      <option value="override">Override</option>
                    </select>
                  ) : (
                    <p className="text-chestnut-charcoal capitalize">{commission.commission_type.replace('_', ' ')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Status</label>
                  {isEditing ? (
                    <select
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                      className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg"
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="paid">Paid</option>
                    </select>
                  ) : (
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[commission.status]}`}>
                      {commission.status}
                    </span>
                  )}
                </div>
              </div>

              <div className="pt-4 border-t border-chestnut-charcoal/10">
                <h3 className="font-medium text-chestnut-charcoal mb-4">Period</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Start Date</label>
                    {isEditing ? (
                      <input
                        type="date"
                        value={form.period_start}
                        onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                        className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg"
                      />
                    ) : (
                      <p className="text-chestnut-charcoal">{commission.period_start ? new Date(commission.period_start).toLocaleDateString() : '—'}</p>
                    )}
                  </div>
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">End Date</label>
                    {isEditing ? (
                      <input
                        type="date"
                        value={form.period_end}
                        onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                        className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg"
                      />
                    ) : (
                      <p className="text-chestnut-charcoal">{commission.period_end ? new Date(commission.period_end).toLocaleDateString() : '—'}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="card p-6 space-y-4">
              <div className="flex items-center gap-3 pb-4 border-b border-chestnut-charcoal/10">
                <div className="p-3 bg-chestnut-gold/20 rounded-lg">
                  <PieChart className="h-6 w-6 text-chestnut-gold" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-chestnut-charcoal">Commission Split</h2>
                  <p className="text-sm text-chestnut-charcoal/60">How this commission is divided between agents</p>
                </div>
              </div>

              <div className="overflow-hidden border border-chestnut-charcoal/10 rounded-lg">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-chestnut-cream/50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-chestnut-charcoal">Role</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-chestnut-charcoal">Agent</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-chestnut-charcoal">Split %</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-chestnut-charcoal">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-chestnut-charcoal/10">
                    <tr>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Writing Agent</span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-chestnut-charcoal">
                        <Link href={`/agents/${commission.agent_id}`} className="text-chestnut-forest hover:underline">
                          {commission.agent_name}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-chestnut-charcoal">
                        {isEditing ? (
                          <span className="text-chestnut-charcoal/60">{form.agent_split_percentage.toFixed ? form.agent_split_percentage.toFixed(2) : form.agent_split_percentage}%</span>
                        ) : (
                          hasSplit ? `${commission.agent_split_percentage}%` : '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-chestnut-forest">
                        {isEditing ? (
                          <div className="relative inline-block">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-chestnut-charcoal/60 text-xs">$</span>
                            <input
                              type="number"
                              value={form.agent_amount}
                              onChange={(e) => updateAgentAmount(parseFloat(e.target.value) || 0)}
                              className="w-28 px-2 pl-5 py-1 border border-chestnut-charcoal/20 rounded text-sm text-right"
                              step="0.01"
                              min="0"
                            />
                          </div>
                        ) : (
                          formatCurrency(commission.agent_amount)
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">Sponsoring Agent</span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-chestnut-charcoal">
                        {commission.parent_agent_name ? (
                          <Link href={`/agents/${commission.parent_agent_id}`} className="text-chestnut-forest hover:underline">
                            {commission.parent_agent_name}
                          </Link>
                        ) : (
                          <span className="text-chestnut-charcoal/40">None</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-chestnut-charcoal">
                        {isEditing ? (
                          <span className="text-chestnut-charcoal/60">{form.sponsor_split_percentage.toFixed ? form.sponsor_split_percentage.toFixed(2) : form.sponsor_split_percentage}%</span>
                        ) : (
                          hasSplit ? `${commission.sponsor_split_percentage}%` : '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-chestnut-gold">
                        {isEditing ? (
                          <div className="relative inline-block">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-chestnut-charcoal/60 text-xs">$</span>
                            <input
                              type="number"
                              value={form.sponsor_amount}
                              onChange={(e) => updateSponsorAmount(parseFloat(e.target.value) || 0)}
                              className="w-28 px-2 pl-5 py-1 border border-chestnut-charcoal/20 rounded text-sm text-right"
                              step="0.01"
                              min="0"
                            />
                          </div>
                        ) : (
                          formatCurrency(commission.sponsor_amount || 0)
                        )}
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr className="bg-chestnut-cream/30 border-t-2 border-chestnut-charcoal/20">
                      <td colSpan={2} className="px-4 py-3 text-sm font-semibold text-chestnut-charcoal">Total</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-chestnut-charcoal">100%</td>
                      <td className="px-4 py-3 text-sm text-right font-bold text-chestnut-charcoal">
                        {formatCurrency(commission.gross_amount)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {commission.supplement_amount > 0 && !isEditing && (
                <div className="mt-4 rounded-lg border border-chestnut-gold/40 bg-chestnut-gold/10 p-4">
                  <h3 className="font-semibold text-chestnut-charcoal mb-2">Chestnut Carrier Supplement</h3>
                  <p className="text-xs text-chestnut-charcoal/70 mb-3">
                    The carrier paid less than the agent's contracted rate, so Chestnut covered the gap to make
                    the agent whole.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-chestnut-charcoal/60">Carrier Payout Rate</p>
                      <p className="text-base font-semibold text-chestnut-charcoal">
                        {commission.carrier_payout_percentage != null
                          ? `${Number(commission.carrier_payout_percentage).toFixed(2)}%`
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-chestnut-charcoal/60">Agent Contracted Rate</p>
                      <p className="text-base font-semibold text-chestnut-charcoal">
                        {commission.agent_split_percentage != null
                          ? `${Number(commission.agent_split_percentage).toFixed(2)}%`
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-chestnut-charcoal/60">Chestnut Supplement</p>
                      <p className="text-base font-bold text-chestnut-gold">
                        {formatCurrency(commission.supplement_amount)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {isEditing && Math.abs((form.agent_amount + form.sponsor_amount) - form.gross_amount) > 0.01 && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  Agent + Sponsor amounts ({formatCurrency(form.agent_amount + form.sponsor_amount)}) must equal the gross amount ({formatCurrency(form.gross_amount)}).
                </div>
              )}
            </div>
          </div>

          <div className="card p-6 space-y-4">
            <h3 className="font-semibold text-chestnut-charcoal border-b border-chestnut-charcoal/10 pb-2">Amounts</h3>
            
            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Gross Amount</label>
              {isEditing ? (
                <input
                  type="number"
                  step="0.01"
                  value={form.gross_amount}
                  onChange={(e) => {
                    const newGross = parseFloat(e.target.value) || 0
                    const agentPct = form.agent_split_percentage || 100
                    const agentAmt = Math.round(newGross * agentPct) / 100
                    const sponsorAmt = newGross - agentAmt
                    setForm({
                      ...form,
                      gross_amount: newGross,
                      agent_amount: agentAmt,
                      sponsor_amount: sponsorAmt
                    })
                  }}
                  className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg"
                />
              ) : (
                <p className="text-2xl font-bold text-chestnut-charcoal">{formatCurrency(commission.gross_amount)}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Agent Amount</label>
              <p className="text-xl font-semibold text-chestnut-forest">{formatCurrency(commission.agent_amount)}</p>
              {hasSplit && (
                <p className="text-xs text-chestnut-charcoal/60 mt-0.5">{commission.agent_split_percentage}% of gross</p>
              )}
            </div>

            {(commission.sponsor_amount || 0) > 0 && (
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Sponsor Amount</label>
                <p className="text-lg font-medium text-chestnut-gold">{formatCurrency(commission.sponsor_amount || 0)}</p>
                {hasSplit && (
                  <p className="text-xs text-chestnut-charcoal/60 mt-0.5">{commission.sponsor_split_percentage}% of gross</p>
                )}
                {commission.parent_agent_name && (
                  <p className="text-sm text-chestnut-charcoal/60 mt-1">
                    To: <Link href={`/agents/${commission.parent_agent_id}`} className="text-chestnut-forest hover:underline">{commission.parent_agent_name}</Link>
                  </p>
                )}
              </div>
            )}

            <div className="pt-4 border-t border-chestnut-charcoal/10">
              <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Created</label>
              <p className="text-sm text-chestnut-charcoal">{new Date(commission.created_at).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {showSplitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-chestnut-charcoal/10 px-6 py-4">
              <h2 className="text-lg font-semibold text-chestnut-charcoal">Split this commission</h2>
              <button onClick={() => setShowSplitModal(false)} className="text-chestnut-charcoal/60 hover:text-chestnut-charcoal">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 px-6 py-5">
              <div className="rounded-lg bg-chestnut-cream/40 p-3 text-sm text-chestnut-charcoal/80">
                Original total agent amount: <strong>{formatCurrency(commission.agent_amount)}</strong>
                <br />
                Primary: <strong>{commission.agent_name}</strong>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-chestnut-charcoal/70">Second agent</label>
                <select
                  value={splitSecondAgentId}
                  onChange={(e) => setSplitSecondAgentId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full rounded-lg border border-chestnut-charcoal/20 px-4 py-2"
                >
                  <option value="">Select agent…</option>
                  {agents
                    .filter((a) => a.id !== commission.agent_id)
                    .map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-chestnut-charcoal/70">Split by</label>
                <div className="inline-flex rounded-lg border border-chestnut-charcoal/20 p-1">
                  <button
                    type="button"
                    onClick={() => switchSplitMode('percent')}
                    className={`px-3 py-1 text-sm rounded ${splitMode === 'percent' ? 'bg-chestnut-forest text-white' : 'text-chestnut-charcoal'}`}
                  >
                    Percent
                  </button>
                  <button
                    type="button"
                    onClick={() => switchSplitMode('dollar')}
                    className={`px-3 py-1 text-sm rounded ${splitMode === 'dollar' ? 'bg-chestnut-forest text-white' : 'text-chestnut-charcoal'}`}
                  >
                    Dollar
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-chestnut-charcoal/70">
                    Primary share {splitMode === 'percent' ? '(%)' : '($)'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={splitPrimaryValue}
                    onChange={(e) => updateSplitPrimary(parseFloat(e.target.value) || 0)}
                    className="w-full rounded-lg border border-chestnut-charcoal/20 px-4 py-2"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-chestnut-charcoal/70">
                    Second share {splitMode === 'percent' ? '(%)' : '($)'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={splitSecondaryValue}
                    onChange={(e) => updateSplitSecondary(parseFloat(e.target.value) || 0)}
                    className="w-full rounded-lg border border-chestnut-charcoal/20 px-4 py-2"
                  />
                </div>
              </div>

              {splitError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{splitError}</div>
              )}

              <p className="text-xs text-chestnut-charcoal/60">
                This reduces the current commission to the primary share and creates a new commission for the second agent.
                Equity credits and overrides are recalculated for both.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-chestnut-charcoal/10 px-6 py-4">
              <button onClick={() => setShowSplitModal(false)} className="btn-secondary" disabled={splitSubmitting}>
                Cancel
              </button>
              <button onClick={submitSplit} className="btn-primary" disabled={splitSubmitting}>
                {splitSubmitting ? 'Splitting…' : 'Split commission'}
              </button>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Commission"
        message="Are you sure you want to delete this commission record? This action cannot be undone."
        deleteUrl={`/commissions/${commission.id}`}
        itemName={`Commission #${commission.policy_number} (${formatCurrency(commission.gross_amount)})`}
        redirectUrl="/commissions"
      />
    </AppLayout>
  )
}
